import React, { useCallback, useRef, useState } from "react";
import { Field } from "formik";
import "./ContactMeForm.scss";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { IOrderFields, PropsContactMeForm } from "../../types";
import { FormikFormComponent } from "../FormikFormComponent";
import { sendMessageGetInTouch } from "../../redux/Reducers/appSlice";
import { MyTextField } from "../MyTextField";
import { validationSchemaContactWithMe } from "../../validationForms";
import { ShowInfoToolTip } from "../ShowInfoToolTip";
import { Turnstile } from "../Turnstile";

const initialValues: IOrderFields = {
  name: "",
  email: "",
  phone: "",
  text: "",
  website: "",
};

const ContactMeForm: React.FC<PropsContactMeForm> = ({ title }) => {
  const dispatch = useAppDispatch();
  const [isTurnstileActive, setIsTurnstileActive] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const formStartedAt = useRef(Date.now());
  const { loading, confirmationGetInTouch, errorGetInTouch } = useAppSelector((state) => state.app);

  const handleTurnstileExpire = useCallback(() => setTurnstileToken(""), []);
  const handleTurnstileVerify = useCallback((token: string) => setTurnstileToken(token), []);
  const activateTurnstile = useCallback(() => setIsTurnstileActive(true), []);

  const handlerSubmitGetInTouch = async (values: IOrderFields): Promise<boolean> => {
    if (!turnstileToken || !values.text) return false;

    try {
      await dispatch(
        sendMessageGetInTouch({
          data: {
            name: values.name,
            email: values.email,
            phone: values.phone,
            text: values.text,
            website: values.website,
            formStartedAt: formStartedAt.current,
            turnstileToken,
          },
        })
      ).unwrap();
      return true;
    } catch {
      return false;
    } finally {
      setTurnstileToken("");
      setTurnstileResetKey((currentKey) => currentKey + 1);
      formStartedAt.current = Date.now();
    }
  };

  return (
    <div className="contactMeForm" onFocusCapture={activateTurnstile} onInputCapture={activateTurnstile}>
      <p className="contactMeForm__eyebrow">Send a note</p>
      <h2 className="contactMeForm__title">{title}</h2>
      <p className="contactMeForm__intro">
        Ask a question, share an idea, or tell me a little about the photos you&apos;re considering.
      </p>
      <ShowInfoToolTip
        error={errorGetInTouch}
        textErrorMessage="Your message could not be sent. Please try again or email me directly."
        textConfirmMessage="Thank you! Your message has been sent. I'll reply as soon as possible."
        confirmation={confirmationGetInTouch}
      />
      <FormikFormComponent
        initialValues={initialValues}
        validationSchema={validationSchemaContactWithMe}
        onSubmit={handlerSubmitGetInTouch}
        buttonProps={{
          title: "Send message",
          style: "brown",
          onDirty: false,
        }}
        loading={loading.sendMessageGetInTouch}
        submitDisabled={!turnstileToken || loading.sendMessageGetInTouch}
      >
        <MyTextField nameLabel="Name *" type="text" name="name" component="input" id="name" />
        <MyTextField nameLabel="Email *" type="email" name="email" component="input" id="email" />
        <MyTextField
          nameLabel={
            <>
              Phone <span className="contactMeForm__optional">(optional)</span>
            </>
          }
          type="tel"
          name="phone"
          component="input"
          id="phone"
        />
        <MyTextField
          nameLabel="Your message *"
          type="text"
          name="text"
          component="textarea"
          id="text"
          placeholder="How can I help?"
        />
        <div className="contactMeForm__honeypot" aria-hidden="true">
          <label htmlFor="website">Website</label>
          <Field id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
        </div>
        {isTurnstileActive ? (
          <Turnstile key={turnstileResetKey} onVerify={handleTurnstileVerify} onExpire={handleTurnstileExpire} />
        ) : (
          <p className="contactMeForm__securityStatus">Security check starts when you begin the form.</p>
        )}
      </FormikFormComponent>
      <p className="contactMeForm__privacy">Your information is private and used only to reply.</p>
    </div>
  );
};

export default ContactMeForm;
